import { useCallback } from 'react';
import {
  avatarState,
  clearAvatarQueue,
  clearNotification,
  sessionState,
  setAppUser,
  setAvatarQueue,
  setChatMode,
  setFirstUserQuery,
  setIsAccessModalShown,
  setOnboardingHintStep,
  useAppDispatch,
  useAppSelector,
} from 'src/store';
import { AppUser, AvatarQueue, ChatMode } from 'src/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserTier } from 'src/types/models/UserTier';
import { updateRenewEarlyStatus } from 'src/store/slices/sessionSlice';
import {
  isLimitedTier,
  isOpenTier,
  isProTier,
  isSubscribedTier,
  isTeamsTier,
} from 'src/utils/user';

export const useSession = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const {
    agent,
    appUser,
    notification,
    isSocketConnected,
    onboardingHintsStep,
    isAccessModalShown,
    isFirstSchedulerTask,
    isFirstUserQuery,
  } = useAppSelector(sessionState);
  const { chatMode, avatarQueue } = useAppSelector(avatarState);

  const dispatch = useAppDispatch();

  const updateChatMode = (mode: ChatMode) => {
    dispatch(setChatMode(mode));

    if (pathname !== '/' && mode !== ChatMode.CALENDAR) {
      navigate('/');
    }
  };

  const removeNotification = () => {
    dispatch(clearNotification());
  };

  const changeOnboardingHintStep = (value: number) => {
    dispatch(setOnboardingHintStep(value));
  };

  const updateAppUser = (user: AppUser) => {
    dispatch(setAppUser(user));
  };

  const updateAvatarQueue = (value?: AvatarQueue) => {
    if (!value) {
      dispatch(clearAvatarQueue());
    } else {
      dispatch(setAvatarQueue(value));
    }
  };

  const setRenewEarlyPending = () => {
    dispatch(
      updateRenewEarlyStatus({
        renew_early_pending_subscription: {
          subscription_id: '',
          period_start: '',
          period_end: '',
        },
      }),
    );
  };

  const updateIsAccessModalShown = (value: boolean) => {
    dispatch(setIsAccessModalShown(value));
  };

  const updateFirstUserQuery = useCallback(() => {
    dispatch(setFirstUserQuery());
  }, [dispatch]);

  return {
    notification,
    removeNotification,
    agent,
    chatMode,
    updateChatMode,
    appUser,
    updateAppUser,
    isSocketConnected,
    onboardingHintsStep,
    changeOnboardingHintStep,
    avatarQueue,
    updateAvatarQueue,
    isAccessModalShown,
    isFirstSchedulerTask,
    updateIsAccessModalShown,
    isFirstUserQuery,
    updateFirstUserQuery,
    /** @deprecated Use from useGetUser instead */
    isOpenTier: isOpenTier(appUser?.tier_id),
    /** @deprecated Use from useGetUser instead */
    isFreeTier: appUser?.tier_id === UserTier.FREE,
    /** @deprecated Use from useGetUser instead */
    isStandardTier: appUser?.tier_id === UserTier.STANDARD,
    /** @deprecated Use from useGetUser instead */
    isStandardTrialTier: appUser?.tier_id === UserTier.STANDARD_TRIAL,
    /** @deprecated Use from useGetUser instead */
    isProTier: isProTier(appUser?.tier_id),
    /** @deprecated Use from useGetUser instead */
    isUltraTier: appUser?.tier_id === UserTier.ULTRA,
    /** @deprecated Use from useGetUser instead */
    isUltraTrialTier: appUser?.tier_id === UserTier.ULTRA_TRIAL,
    /** @deprecated Use from useGetUser instead */
    isSubscribedTier: isSubscribedTier(appUser?.tier_id),
    /** @deprecated Use from useGetUser instead */
    // TODO(olha): experimental variable.
    /** @deprecated Use from useGetUser instead */
    isLimitedTier: isLimitedTier(appUser?.tier_id),
    /** @deprecated Use from useGetUser instead */
    isProTrialTier: appUser?.tier_id === UserTier.PRO_TRIAL,
    /** @deprecated Use from useGetUser instead */
    isTestTier: appUser?.tier_id === UserTier.TESTING,
    /** @deprecated Use from useGetUser instead */
    isEnterpriseTrialTier: appUser?.tier_id === UserTier.ENTERPRISE_TRIAL,
    /** @deprecated Use from useGetUser instead */
    isEnterpriseTier: appUser?.tier_id === UserTier.ENTERPRISE,
    /** @deprecated Use from useGetUser instead */
    isTeamsTier: isTeamsTier(appUser?.tier_id),
    /** @deprecated Use from useGetUser instead */
    isTeamsProTier: appUser.tier_id === UserTier.TEAMS_PRO,
    /** @deprecated Use from useGetUser instead */
    isTeamsStandardTier: appUser?.tier_id === UserTier.TEAMS_STANDARD,
    /** @deprecated Use from useGetUser instead */
    isTeamsUltraTier: appUser?.tier_id === UserTier.TEAMS_ULTRA,
    /** @deprecated Use from useGetUser instead */
    tierId: appUser?.tier_id || UserTier.OPEN,
    setRenewEarlyPending,
  };
};
